import { useCallback } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { Tabs } from '@bambi/component-library/src/components/Tabs/Tabs';
import { ArrowPathIcon, ClipboardIcon } from '@heroicons/react/24/outline';

import { useAppSelector } from 'src/app/store/hooks';
import { useInvoicesPaymentLinkRetrieveQuery } from 'src/common/external/bambi-api/bambiApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { FormFieldDefault } from 'src/common/FormField/FormFieldDefault';
import { Modal, ModalProps } from 'src/common/primitives/Modal';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { useCopyText } from './hooks/useCopyText';
import { usePaymentLink } from './hooks/usePaymentLink';
import { EmailFormTab } from './PaymentLink/Tab/EmailFormTab';
import { QRCodeTab } from './PaymentLink/Tab/QRCodeTab';
import { PaymentLinkGeneratePDFInvoiceButton } from './PaymentLinkGeneratePDFInvoiceButton';

interface PaymentLinkModalProps extends Pick<ModalProps, 'open' | 'setOpen'> {
  payerId: string;
  payerType: 'passenger' | 'payer_organization';
}
export function PaymentLinkModal({
  open,
  setOpen,
  payerId,
  payerType,
}: PaymentLinkModalProps) {
  const handleCopyText = useCopyText();
  const editingInvoice = useAppSelector(
    (state) => state.invoice.editingInvoice
  );
  const editingPaymentLinkId = useAppSelector(
    (state) => state.invoice.editingPaymentLinkId
  );

  const {
    isLoading: isLoadingPaymentLink,
    data: paymentLinkData,
    error,
  } = useInvoicesPaymentLinkRetrieveQuery(
    {
      id: editingPaymentLinkId ?? '',
    },
    {
      skip: !editingPaymentLinkId,
    }
  );

  const { isLoading: isLoadingPaymentLinkRegenerate, createPaymentLink } =
    usePaymentLink(editingInvoice ?? null, true);

  const handleRegeneratePaymentLink = useCallback(() => {
    createPaymentLink({
      payerId: payerId,
      payerType: payerType,
    });
  }, [createPaymentLink, payerId, payerType]);

  if (!editingInvoice) {
    return (
      <Modal
        open={open}
        setOpen={setOpen}
        contentClassnames="max-w-[30vw]"
        title="Error retrieving invoice"
      >
        <FormErrorMessage>
          Something unexpected happened: unable to find the editing invoice
        </FormErrorMessage>
      </Modal>
    );
  }

  const shouldShowRegenerateButton =
    paymentLinkData?.amount_cents !== editingInvoice.total_amount_due_cents;

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      contentClassnames="max-w-[30vw]"
      title="Payment Link"
    >
      <h3 className="pb-2 text-[24px] font-medium leading-6 text-gray-900">
        Payment Link
      </h3>
      <p>
        Share the link below with your customer so they can make a payment. The
        link is valid for 30 days. It can only be used for full payments (not
        partial ones).
      </p>
      {isLoadingPaymentLink ? (
        <div className="flex flex-row justify-center">
          <LoadingIndicator />
        </div>
      ) : !paymentLinkData ? (
        <div data-testid="form-error-message">
          <FormErrorMessage>
            Unable to retrieve payment link: {formatServerError(error)}
          </FormErrorMessage>
        </div>
      ) : (
        <>
          <div className="mt-4 flex w-full flex-row items-center">
            <div className="flex w-full flex-row items-center">
              <FormFieldDefault
                inputProps={{
                  value: paymentLinkData.payment_portal_url,
                  readOnly: true,
                  onFocus: (e) => {
                    e.target.select();
                  },
                }}
                type="text"
                className="w-full"
                error={
                  shouldShowRegenerateButton
                    ? 'The invoice due amount is different from the payment link amount. Regenerate the payment link to ensure the correct amount is displayed on the payment link form, the URL will be different.'
                    : undefined
                }
              />
              <Button
                variant="icon"
                onClick={() =>
                  handleCopyText(paymentLinkData.payment_portal_url)
                }
              >
                <ClipboardIcon className="w-6" />
              </Button>
              {shouldShowRegenerateButton && (
                <Button
                  variant="icon"
                  onClick={handleRegeneratePaymentLink}
                  loading={isLoadingPaymentLinkRegenerate}
                  dataTestId="regenerate-payment-link-button"
                >
                  <ArrowPathIcon className="w-6" />
                </Button>
              )}
            </div>
          </div>
          <div className="mt-4 flex flex-row items-center justify-between">
            <h4 className="text-md font-medium">Sharing options</h4>
            <PaymentLinkGeneratePDFInvoiceButton
              invoiceId={editingInvoice.id}
              invoiceNumber={editingInvoice.number}
              uploadedInvoiceFile={paymentLinkData.invoice_file}
            />
          </div>
          <Tabs
            defaultValue="email"
            tabs={[
              {
                content: (
                  <EmailFormTab
                    paymentLinkId={paymentLinkData.id}
                    paymentLinkUrl={paymentLinkData.payment_portal_url}
                    hasInvoiceAttached={!!paymentLinkData.invoice_file}
                    invoiceNumber={editingInvoice.number}
                    payerId={payerId}
                    payerType={payerType}
                    onClose={() => setOpen(false)}
                  />
                ),
                label: 'Email',
                value: 'email',
              },
              {
                content: (
                  <QRCodeTab
                    url={paymentLinkData.payment_portal_url}
                    onClose={() => setOpen(false)}
                  />
                ),
                label: 'QR Code',
                value: 'qr-code',
              },
            ]}
          />
        </>
      )}
    </Modal>
  );
}
